import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import api from '../lib/api';
import EmployeeShiftBoard from './EmployeeShiftBoard';
import MyShifts from './MyShifts';
import PolicyHub from './PolicyHub';
import LMSTrainingCenter from './LMSTrainingCenter';
import ShiftToolkit from './ShiftToolkit';
import TimesheetForm from './TimesheetForm';
import NotificationBell from './NotificationBell';
import MyPatients from './MyPatients';
import MyCertificates from './MyCertificates';
import ShiftCoverage from './ShiftCoverage';
import { TutorialHelpIcon } from './TutorialOverlay';
import { FileText, Download, LogOut, Heart, Clock, BookOpen, Calendar, Briefcase, FolderOpen, GraduationCap, Clipboard, FileSpreadsheet, Users, Award, HandHelping } from 'lucide-react';

export default function CaregiverDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const response = await api.get('/policies');
      setPolicies(response.data);
    } catch (error) {
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Premium Navigation Bar */}
      <nav className="nav-premium sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_caregiver-command/artifacts/3ip58oau_Integrity%20Home%20Care%20Logo%20NEW.png"
                alt="Integrity Home Care LLC"
                className="h-8 sm:h-11 w-auto"
              />
              <div className="hidden sm:block border-l border-purple-200 pl-4">
                <h1 className="text-lg font-bold text-purple-900 tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Caregiver Portal
                </h1>
                <p className="text-xs text-gray-500 font-medium">Integrity Home Care LLC</p>
              </div>
              <div className="sm:hidden">
                <h1 className="text-sm font-bold text-purple-900">Caregiver</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-5">
              <div className="text-right border-l border-purple-100 pl-3 sm:pl-5">
                <p className="text-xs sm:text-sm font-semibold text-purple-900">{user?.full_name?.split(' ')[0]}</p>
                <p className="text-xs text-gray-500 capitalize hidden sm:block font-medium">{user?.role}</p>
              </div>
              <TutorialHelpIcon />
              <NotificationBell />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="caregiver-logout-button"
                className="text-gray-500 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-all duration-200 p-2"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Tab Navigation - Premium Style */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-purple-100/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="flex gap-0.5 sm:gap-1 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-3 sm:px-4 font-medium transition-all duration-200 whitespace-nowrap text-sm sm:text-base relative ${
                activeTab === 'overview'
                  ? 'text-purple-900'
                  : 'text-gray-500 hover:text-purple-700'
              }`}
            >
              Overview
              {activeTab === 'overview' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-600 rounded-full" />}
            </button>
            <button
              onClick={() => setActiveTab('shifts')}
              className={`py-4 px-3 sm:px-4 font-medium transition-all duration-200 flex items-center gap-1.5 sm:gap-2 whitespace-nowrap text-sm sm:text-base relative ${
                activeTab === 'shifts'
                  ? 'text-purple-900'
                  : 'text-gray-500 hover:text-purple-700'
              }`}
            >
              <Briefcase className="w-4 h-4 hidden sm:block" />
              Available Shifts
              {activeTab === 'shifts' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-600 rounded-full" />}
            </button>
            <button
              onClick={() => setActiveTab('myshifts')}
              className={`py-4 px-3 sm:px-4 font-medium transition-all duration-200 flex items-center gap-1.5 sm:gap-2 whitespace-nowrap text-sm sm:text-base relative ${
                activeTab === 'myshifts'
                  ? 'text-purple-900'
                  : 'text-gray-500 hover:text-purple-700'
              }`}
            >
              <Calendar className="w-4 h-4 hidden sm:block" />
              My Shifts
              {activeTab === 'myshifts' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-600 rounded-full" />}
            </button>
            <button
              onClick={() => setActiveTab('patients')}
              className={`py-4 px-3 sm:px-4 font-medium transition-all duration-200 flex items-center gap-1.5 sm:gap-2 whitespace-nowrap text-sm sm:text-base relative ${
                activeTab === 'patients'
                  ? 'text-purple-900'
                  : 'text-gray-500 hover:text-purple-700'
              }`}
              data-testid="patients-tab"
            >
              <Users className="w-4 h-4 hidden sm:block" />
              My Patients
              {activeTab === 'patients' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-600 rounded-full" />}
            </button>
            <button
              onClick={() => setActiveTab('policies')}
              className={`py-4 px-3 sm:px-4 font-medium transition-all duration-200 flex items-center gap-1.5 sm:gap-2 whitespace-nowrap text-sm sm:text-base relative ${
                activeTab === 'policies'
                  ? 'text-purple-900'
                  : 'text-gray-500 hover:text-purple-700'
              }`}
              data-testid="policies-tab"
            >
              <FolderOpen className="w-4 h-4 hidden sm:block" />
              Policies
              {activeTab === 'policies' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-600 rounded-full" />}
            </button>
            <button
              onClick={() => setActiveTab('training')}
              className={`py-4 px-3 sm:px-4 font-medium transition-all duration-200 flex items-center gap-1.5 sm:gap-2 whitespace-nowrap text-sm sm:text-base relative ${
                activeTab === 'training'
                  ? 'text-purple-900'
                  : 'text-gray-500 hover:text-purple-700'
              }`}
              data-testid="training-tab"
            >
              <GraduationCap className="w-4 h-4 hidden sm:block" />
              Training
              {activeTab === 'training' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-600 rounded-full" />}
            </button>
            <button
              onClick={() => setActiveTab('certificates')}
              className={`py-4 px-3 sm:px-4 font-medium transition-all duration-200 flex items-center gap-1.5 sm:gap-2 whitespace-nowrap text-sm sm:text-base relative ${
                activeTab === 'certificates'
                  ? 'text-purple-900'
                  : 'text-gray-500 hover:text-purple-700'
              }`}
              data-testid="certificates-tab"
            >
              <Award className="w-4 h-4 hidden sm:block" />
              Certificates
              {activeTab === 'certificates' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-600 rounded-full" />}
            </button>
            <button
              onClick={() => setActiveTab('toolkit')}
              className={`py-4 px-3 sm:px-4 font-medium transition-all duration-200 flex items-center gap-1.5 sm:gap-2 whitespace-nowrap text-sm sm:text-base relative ${
                activeTab === 'toolkit'
                  ? 'text-purple-900'
                  : 'text-gray-500 hover:text-purple-700'
              }`}
              data-testid="toolkit-tab"
            >
              <Clipboard className="w-4 h-4 hidden sm:block" />
              Toolkit
              {activeTab === 'toolkit' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-600 rounded-full" />}
            </button>
            <button
              onClick={() => setActiveTab('timesheet')}
              className={`py-4 px-3 sm:px-4 font-medium transition-all duration-200 flex items-center gap-1.5 sm:gap-2 whitespace-nowrap text-sm sm:text-base relative ${
                activeTab === 'timesheet'
                  ? 'text-purple-900'
                  : 'text-gray-500 hover:text-purple-700'
              }`}
              data-testid="timesheet-tab"
            >
              <FileSpreadsheet className="w-4 h-4 hidden sm:block" />
              Timesheet
              {activeTab === 'timesheet' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-600 rounded-full" />}
            </button>
            <button
              onClick={() => setActiveTab('coverage')}
              className={`relative flex items-center gap-1.5 px-3 sm:px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'coverage'
                  ? 'text-purple-700'
                  : 'text-gray-600 hover:text-purple-600'
              }`}
              data-testid="coverage-tab"
            >
              <HandHelping className="w-4 h-4 hidden sm:block" />
              Coverage
              {activeTab === 'coverage' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-600 rounded-full" />}
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-6 sm:py-10">
        {activeTab === 'overview' ? (
        <>
        <div className="mb-10">
          <div className="card-premium p-6 sm:p-8 bg-gradient-to-r from-purple-600 to-purple-700 border-0 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold mb-2 tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Welcome back, {user?.full_name}!
                </h2>
                <p className="text-purple-100 text-sm sm:text-base">Access policies, procedures, and important documents</p>
              </div>
              <div className="hidden md:block p-4 bg-white/10 rounded-2xl backdrop-blur-sm">
                <Heart className="w-12 h-12 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          <div className="card-stat group">
            <div className="flex items-center gap-4">
              <div className="icon-container">
                <BookOpen className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-900 tracking-tight">{policies.length}</p>
                <p className="text-sm text-gray-500 font-medium">Available Policies</p>
              </div>
            </div>
          </div>

          <div className="card-stat group">
            <div className="flex items-center gap-4">
              <div className="icon-container">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-900 tracking-tight">-</p>
                <p className="text-sm text-gray-500 font-medium">Hours This Week</p>
              </div>
            </div>
          </div>

          <div className="card-stat group">
            <div className="flex items-center gap-4">
              <div className="icon-container">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-900 tracking-tight">-</p>
                <p className="text-sm text-gray-500 font-medium">Forms Completed</p>
              </div>
            </div>
          </div>
        </div>

        <div className="section-header">
          <h2 className="section-title">Policies & Procedures</h2>
          <p className="section-subtitle">View and download company policies</p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : policies.length === 0 ? (
          <div className="card-premium p-12 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-purple-50 rounded-full">
                <FileText className="w-12 h-12 text-purple-400" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-purple-900 mb-2">No policies available</h3>
            <p className="text-gray-500">Check back soon for important company documents</p>
          </div>
        ) : (
          <div className="space-y-4">
            {policies.map((policy) => (
              <div
                key={policy.id}
                className="card-premium p-6 hover:border-purple-200"
                data-testid={`policy-card-${policy.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="icon-container flex-shrink-0">
                      <FileText className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-lg text-purple-900 mb-1 truncate">
                        {policy.title}
                      </h3>
                      {policy.description && (
                        <p className="text-sm text-gray-500 mb-2 line-clamp-1">
                          {policy.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-gray-400 font-medium">
                        <span>Uploaded {formatDate(policy.uploaded_at)}</span>
                        <span>•</span>
                        <span>{formatFileSize(policy.file_size)}</span>
                        {policy.uploader_name && (
                          <>
                            <span>•</span>
                            <span>By {policy.uploader_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => window.open(policy.file_url, '_blank')}
                    data-testid={`view-policy-${policy.id}`}
                    className="ml-4 btn-primary flex-shrink-0"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    View
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        </>
        ) : activeTab === 'shifts' ? (
          <div className="card-premium p-6">
            <EmployeeShiftBoard />
          </div>
        ) : activeTab === 'myshifts' ? (
          <div className="card-premium p-6">
            <MyShifts />
          </div>
        ) : activeTab === 'patients' ? (
          <MyPatients />
        ) : activeTab === 'policies' ? (
          <div>
            <div className="section-header">
              <h2 className="section-title">Policies & Procedures</h2>
              <p className="section-subtitle">View important company documents</p>
            </div>
            <div className="card-premium p-6">
              <PolicyHub isAdmin={false} />
            </div>
          </div>
        ) : activeTab === 'training' ? (
          <div className="card-premium p-6">
            <LMSTrainingCenter />
          </div>
        ) : activeTab === 'certificates' ? (
          <div className="card-premium p-6">
            <MyCertificates />
          </div>
        ) : activeTab === 'toolkit' ? (
          <div className="card-premium p-6">
            <ShiftToolkit />
          </div>
        ) : activeTab === 'timesheet' ? (
          <div className="card-premium p-6">
            <TimesheetForm />
          </div>
        ) : activeTab === 'coverage' ? (
          <div className="card-premium p-6">
            <ShiftCoverage userRole={user?.role} />
          </div>
        ) : null}
      </main>
    </div>
  );
}